'use client';

import React, { memo } from 'react';
import clsx from 'clsx';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import ButtonLandingPage from '@/components/landing/ButtonLandingPage';

export type StepVM = {
    _key: string;
    stepLabel: string | null;
    title: string | null;
    description: string | null;
};

export type StepsSectionClientProps = {
    heading: string;
    steps: StepVM[];
};

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.7, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

// TABLET: 768..1023
// DESKTOP: >=1024
const DESKTOP_LAYOUT_BY_INDEX: Record<number, string> = {
    0: '[@media(min-width:1024px)]:col-start-2 [@media(min-width:1024px)]:row-start-1',
    1: '[@media(min-width:1024px)]:col-start-3 [@media(min-width:1024px)]:row-start-1',
    2: '[@media(min-width:1024px)]:col-start-1 [@media(min-width:1024px)]:row-start-2',
    3: '[@media(min-width:1024px)]:col-start-2 [@media(min-width:1024px)]:row-start-2',
    4: '[@media(min-width:1024px)]:col-start-3 [@media(min-width:1024px)]:row-start-2',
};

const TABLET_LAYOUT_BY_INDEX: Record<number, string> = {
    // rząd 1: nagłówek (col 1) + karta 1 (col 2)
    0: '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-2 [@media(min-width:768px)_and_(max-width:1023px)]:row-start-1',
    // rząd 2: karta 2 + karta 3
    1: '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-1 [@media(min-width:768px)_and_(max-width:1023px)]:row-start-2',
    2: '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-2 [@media(min-width:768px)_and_(max-width:1023px)]:row-start-2',
    // rząd 3: karta 4 + karta 5 / CTA
    3: '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-1 [@media(min-width:768px)_and_(max-width:1023px)]:row-start-3',
    4: '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-2 [@media(min-width:768px)_and_(max-width:1023px)]:row-start-3',
};

function StepsSectionClientBase({ heading, steps }: StepsSectionClientProps) {
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="px-[5%] py-16 md:py-10 lg:py-14 bg-secondary text-primary overflow-hidden rounded-b-3xl"
            >
                <div className="container">
                    <div
                        className={clsx(
                            'grid grid-cols-1 gap-2',
                            // TABLET: 2 kolumny
                            '[@media(min-width:768px)_and_(max-width:1023px)]:grid-cols-2 [@media(min-width:768px)_and_(max-width:1023px)]:gap-6',
                            // DESKTOP: 3 kolumny
                            '[@media(min-width:1024px)]:grid-cols-3 [@media(min-width:1024px)]:gap-6 lg:gap-5'
                        )}
                    >
                        {/* MOBILE heading (<768) */}
                        {heading && (
                            <m.h2
                                variants={slideUp}
                                custom={0}
                                className={clsx(
                                    'mb-8 max-w-xl text-2xl font-medium leading-tight',
                                    '[@media(min-width:768px)]:hidden'
                                )}
                            >
                                {heading}
                            </m.h2>
                        )}

                        {/* TABLET heading (768..1023) */}
                        {heading && (
                            <m.div
                                variants={slideUp}
                                custom={0}
                                className={clsx(
                                    'hidden',
                                    '[@media(min-width:768px)_and_(max-width:1023px)]:block',
                                    '[@media(min-width:768px)_and_(max-width:1023px)]:col-start-1',
                                    '[@media(min-width:768px)_and_(max-width:1023px)]:row-start-1',
                                    '[@media(min-width:768px)_and_(max-width:1023px)]:self-start'
                                )}
                            >
                                <h2 className="text-2xl font-medium leading-tight max-w-sm">{heading}</h2>
                            </m.div>
                        )}

                        {/* DESKTOP heading (>=1024) */}
                        {heading && (
                            <m.div
                                variants={slideUp}
                                custom={0}
                                className={clsx(
                                    'hidden',
                                    '[@media(min-width:1024px)]:block',
                                    '[@media(min-width:1024px)]:col-start-1',
                                    '[@media(min-width:1024px)]:row-start-1'
                                )}
                            >
                                <h2 className="text-xl font-medium md:text-4xl md:max-w-sm">{heading}</h2>
                            </m.div>
                        )}

                        {/* CARDS */}
                        {steps.map((step, idx) => {
                            const isCtaCard = !step.stepLabel;

                            return (
                                <m.article
                                    key={step._key}
                                    variants={slideUp}
                                    custom={idx + 1}
                                    className={clsx(
                                        'relative rounded-3xl transition-colors duration-500 flex flex-col overflow-hidden',
                                        // padding/height
                                        'p-4 h-[350px]',
                                        '[@media(min-width:768px)_and_(max-width:1023px)]:p-6 [@media(min-width:768px)_and_(max-width:1023px)]:h-[420px]',
                                        '[@media(min-width:1024px)]:p-6 [@media(min-width:1024px)]:h-[420px]',
                                        isCtaCard ? 'justify-between' : 'justify-end',

                                        // ✅ FIX: Wymuszamy brak ramki (border-0) dla kart z tłem.
                                        // Wcześniej było 'border-secondary-light', co na iOS/iPad mogło rysować linię 1px.
                                        isCtaCard
                                            ? 'border border-secondary-light bg-transparent'
                                            : 'border-0 bg-secondary-light',

                                        TABLET_LAYOUT_BY_INDEX[idx] ?? '',
                                        DESKTOP_LAYOUT_BY_INDEX[idx] ?? ''
                                    )}
                                >
                                    {step.stepLabel && (
                                        <span className="pointer-events-none absolute right-5 top-5 text-sm tracking-[0.15em] text-primary uppercase">
                      {step.stepLabel}
                    </span>
                                    )}

                                    {step.title && (
                                        <h3 className={clsx('text-3xl font-medium', 'mb-3', '[@media(min-width:768px)]:mb-4')}>
                                            {step.title}
                                        </h3>
                                    )}

                                    <div className={isCtaCard ? 'mt-auto' : ''}>
                                        {step.description && <p className="text-base">{step.description}</p>}
                                        {isCtaCard && (
                                            <div className="mt-6">
                                                <ButtonLandingPage label="Napisz do nas" />
                                            </div>
                                        )}
                                    </div>
                                </m.article>
                            );
                        })}
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}

export default memo(StepsSectionClientBase);